/**
 * Prefill helpers for the GPL registration form.
 *
 * The logged-in employee's details live in two places, and neither is the top level of
 * the auth user - `state.auth.user` only carries empId/username/empCode/role/layout/
 * permissions/details (see authService.authenticateUser in the backend):
 *
 *  1. The persisted login payload: `user.details.travelData.userDetails`
 *     Available immediately (redux-persist mirrors the auth slice into localStorage),
 *     but uses ERP naming - `empEmail`, `contactNumber` - and carries no photo.
 *
 *  2. The employee profile API (`useFetchProfileQuery(empCode)`)
 *     Authoritative for names and the photo, and the only source that resolves
 *     `employeePhotoPath` to a full URL. Its phone number is not a top-level field - it
 *     sits on the self-entered contact card, `contact[0].phoneNumber` (see Profile.jsx).
 *
 * extractPrefill merges the two so the form is populated on first paint. Three exceptions
 * to "profile API wins": the email and the phone number both come from the ERP payload
 * (`empEmail` / `contactNumber`), and the photo can only come from the profile API.
 */

const PERSIST_KEY = 'persist:root';

const isFilled = (value) => value !== undefined && value !== null && String(value).trim() !== '';

const firstFilled = (...values) => values.find(isFilled) ?? '';

/** Reads the persisted auth user straight out of localStorage. */
export const readUserFromStorage = () => {
  try {
    const root = window.localStorage.getItem(PERSIST_KEY);
    if (!root) return null;

    // redux-persist stores each whitelisted slice as its own JSON string.
    const slices = JSON.parse(root);
    if (!slices?.auth) return null;

    const auth = typeof slices.auth === 'string' ? JSON.parse(slices.auth) : slices.auth;
    return auth?.user || null;
  } catch (error) {
    // Corrupt or absent storage must never break the form.
    return null;
  }
};

/** Prefers the rehydrated Redux user, falling back to localStorage. */
export const resolveUser = (reduxUser) => {
  if (reduxUser && Object.keys(reduxUser).length > 0) return reduxUser;
  return readUserFromStorage();
};

/** The ERP employee block inside the persisted login payload. */
export const getErpDetails = (authUser) => authUser?.details?.travelData?.userDetails || {};

/**
 * Parses the date-of-birth strings the employee records actually contain.
 *
 * `new Date(value)` alone is not enough: employee dates are written day-first
 * (see Birthday.jsx, which splits them as day/month/year), and JS reads "25/12/1999"
 * as month 25 - NaN - which silently blanked the Age field for anyone born after the
 * 12th of a month. Day-first formats are therefore parsed explicitly.
 */
const parseDob = (value) => {
  if (!value) return null;
  const text = String(value).trim();

  // ISO first: 1987-05-03 or 1987-05-03T00:00:00
  const iso = text.match(/^(\d{4})-(\d{1,2})-(\d{1,2})/);
  if (iso) {
    const [, year, month, day] = iso.map(Number);
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  // Day-first: 03/05/1987, 25-12-1999, 13.07.1995
  const dayFirst = text.match(/^(\d{1,2})[/\-.](\d{1,2})[/\-.](\d{4})/);
  if (dayFirst) {
    const [, day, month, year] = dayFirst.map(Number);
    if (month < 1 || month > 12 || day < 1 || day > 31) return null;
    const date = new Date(year, month - 1, day);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  const fallback = new Date(text);
  return Number.isNaN(fallback.getTime()) ? null : fallback;
};

/**
 * The API's confusingly named `Dob` field wants an integer age in years, not a date.
 * Returns '' when no candidate parses, so the field stays editable rather than wrong.
 */
export const deriveAge = (...dobCandidates) => {
  const today = new Date();

  for (const candidate of dobCandidates) {
    const birth = parseDob(candidate);
    if (!birth) continue;

    let age = today.getFullYear() - birth.getFullYear();
    const monthsFromBirthday = today.getMonth() - birth.getMonth();
    // Not had this year's birthday yet, so knock a year off.
    if (monthsFromBirthday < 0 || (monthsFromBirthday === 0 && today.getDate() < birth.getDate())) {
      age -= 1;
    }
    if (age > 0 && age < 100) return String(age);
  }
  return '';
};

/**
 * The API accepts only the exact strings "Male" / "Female", while employee records use
 * assorted spellings ("M", "female", "MALE"). Anything unrecognised returns '' so the
 * user picks it themselves rather than being assigned a value we guessed.
 */
const normaliseGender = (value) => {
  const text = String(value || '').trim().toLowerCase();
  if (text === 'm' || text === 'male') return 'Male';
  if (text === 'f' || text === 'female') return 'Female';
  return '';
};

/** Splits a single full name into first / last, ignoring any middle parts. */
const splitFullName = (fullName) => {
  const parts = String(fullName || '')
    .trim()
    .split(/\s+/)
    .filter(Boolean);
  if (parts.length === 0) return { first: '', last: '' };
  if (parts.length === 1) return { first: parts[0], last: '' };
  return { first: parts[0], last: parts[parts.length - 1] };
};

/**
 * Normalises the employee record down to the values the GPL form needs.
 * `profile` is the employee profile API response; `authUser` the persisted login user.
 * Everything comes back as a string, ready for Formik.
 */
export const extractPrefill = (profile, authUser) => {
  const p = profile || {};
  const erp = getErpDetails(authUser);

  // Last resort: some records only carry a single combined name.
  const fallbackName = splitFullName(firstFilled(p.empName, erp.empName, erp.employeeName));

  return {
    firstName: firstFilled(p.firstName, erp.firstName, fallbackName.first),
    lastName: firstFilled(p.lastName, erp.lastName, fallbackName.last),
    // Always the ERP payload's `empEmail` - deliberately not the profile API's `email`,
    // which can hold the same address in different casing.
    email: firstFilled(erp.empEmail),
    // The employee's own number is the ERP payload's `contactNumber`.
    //
    // Deliberately NOT `empPhoneNo`: that key carries the same value as
    // `emergencyContactNo`, so reading it prefilled the form with the emergency
    // contact's number instead of the player's own - and PhoneNo is a locked field, so
    // the player could not correct it. `phoneNo` / `mobileNo` were never real keys on
    // either payload and are gone with it.
    //
    // The profile API's fallback is its self-entered contact card, which is nested
    // rather than top-level (the same path Profile.jsx reads).
    phone: firstFilled(erp.contactNumber, p.contact?.[0]?.phoneNumber),
    gender: normaliseGender(firstFilled(p.gender, erp.gender)),
    // Age, already converted. Both sources are offered because they can differ in
    // format, and one may parse where the other does not.
    age: deriveAge(p.dob, erp.dob),
    // Only the profile API resolves the photo to a usable URL.
    photo: firstFilled(p.employeePhotoPath),
  };
};
